const { commitCashItemDay } = require('./cashItemDay');

function pad(number) {
  return String(number).padStart(2, '0');
}

function isoDate(year, month, day) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function shiftMonth(year, month, delta) {
  const shifted = new Date(year, month - 1 + delta, 1);
  return [shifted.getFullYear(), shifted.getMonth() + 1];
}

function nextYmd(year, month, day) {
  if (day < daysInMonth(year, month)) return [year, month, day + 1];
  if (month === 12) return [year + 1, 1, 1];
  return [year, month + 1, 1];
}

function compareYmd(left, right) {
  if (left[0] !== right[0]) return left[0] - right[0];
  if (left[1] !== right[1]) return left[1] - right[1];
  return left[2] - right[2];
}

function cycleStartMonth(dateStr) {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  if (day >= 25) return [year, month];
  return shiftMonth(year, month, -1);
}

function currentCycleRange(dateStr) {
  const [cycleYear, cycleMonth] = cycleStartMonth(dateStr);
  const [endYear, endMonth] = shiftMonth(cycleYear, cycleMonth, 1);
  return {
    start: isoDate(cycleYear, cycleMonth, 25),
    end: isoDate(endYear, endMonth, 24),
  };
}

function previousCycleRange(dateStr) {
  const current = currentCycleRange(dateStr);
  const [startYear, startMonth] = current.start.split('-').map(Number);
  const [prevYear, prevMonth] = shiftMonth(startYear, startMonth, -1);
  return {
    start: isoDate(prevYear, prevMonth, 25),
    end: isoDate(startYear, startMonth, 24),
  };
}

function nextIsoDate(dateStr) {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  const [nextYear, nextMonth, nextDay] = nextYmd(year, month, day);
  return isoDate(nextYear, nextMonth, nextDay);
}

/** Snapshot date for the cash-flow title, as month/day with no leading zeros. */
function cashFlowAsOfLabel(dateStr) {
  const match = String(dateStr || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!month || !day) return '';
  return `${month}/${day}`;
}

function projectionMonthLastDate(asOfDate) {
  const cycle = currentCycleRange(asOfDate);
  const [year, month] = String(cycle.end || '').split('-').map(Number);
  if (!year || !month) return '';
  return isoDate(year, month, daysInMonth(year, month));
}

function projectionFetchEnd(asOfDate) {
  const last = projectionMonthLastDate(asOfDate);
  return last ? nextIsoDate(last) : '';
}

function projectionMonthDate(asOfDate, day) {
  const dayNum = Number(day);
  if (!Number.isInteger(dayNum) || dayNum < 1 || dayNum > 31) return '';
  const cycle = currentCycleRange(asOfDate);
  const [year, month] = String(cycle.end || '').split('-').map(Number);
  if (!year || !month || dayNum > daysInMonth(year, month)) return '';
  return isoDate(year, month, dayNum);
}

function roundCents(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function transactionDate(transaction) {
  const raw = transaction && (transaction.BankRefDatetime || transaction.date);
  const date = String(raw || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : '';
}

function cumulativeDepositByDay(transactions, cycleStart, cycleEnd) {
  const credits = {};
  (transactions || []).forEach((transaction) => {
    const date = transactionDate(transaction);
    if (!date || date < cycleStart || date > cycleEnd) return;
    const credit = parseFloat(transaction.Credit ?? transaction.credit);
    credits[date] = (credits[date] || 0) + (Number.isFinite(credit) ? credit : 0);
  });

  const byDay = {};
  for (let day = 1; day <= 31; day += 1) byDay[day] = 0;

  let cursor = cycleStart.split('-').map(Number);
  const end = cycleEnd.split('-').map(Number);
  let running = 0;
  while (compareYmd(cursor, end) <= 0) {
    const key = isoDate(cursor[0], cursor[1], cursor[2]);
    running += credits[key] || 0;
    byDay[cursor[2]] = running;
    cursor = nextYmd(cursor[0], cursor[1], cursor[2]);
  }
  return byDay;
}

function forecastDepositByDay(asOfDate, previousByDay, transactions) {
  const cycle = currentCycleRange(asOfDate);
  const [projectionYear, projectionMonth] = cycle.end.split('-').map(Number);
  const asOfDay = Number(String(asOfDate).split('-')[2]);
  const credits = {};
  (transactions || []).forEach((transaction) => {
    const date = transactionDate(transaction);
    if (!date || date < cycle.start || date > asOfDate) return;
    const credit = parseFloat(transaction.Credit ?? transaction.credit);
    credits[date] = roundCents(
      (credits[date] || 0) + (Number.isFinite(credit) ? credit : 0),
    );
  });

  let running = 0;
  const actualOnDate = {};
  let cursor = cycle.start.split('-').map(Number);
  const asOf = String(asOfDate).split('-').map(Number);
  while (compareYmd(cursor, asOf) <= 0) {
    const key = isoDate(cursor[0], cursor[1], cursor[2]);
    running = roundCents(running + (credits[key] || 0));
    actualOnDate[key] = running;
    cursor = nextYmd(cursor[0], cursor[1], cursor[2]);
  }

  const previous = (day) => roundCents(Number(previousByDay && previousByDay[day]) || 0);
  const shift = roundCents(running - previous(asOfDay));
  const forecast = {};
  for (let day = 1; day <= 24; day += 1) {
    const date = isoDate(projectionYear, projectionMonth, day);
    forecast[day] = date <= asOfDate
      ? (actualOnDate[date] != null ? actualOnDate[date] : running)
      : roundCents(previous(day) + shift);
  }
  for (let day = 25; day <= 31; day += 1) {
    forecast[day] = roundCents(forecast[24] + previous(day));
  }
  return forecast;
}

function snapshotAmount(value) {
  if (value == null || value === '') return 0;
  const amount = parseFloat(String(value).replace(/,/g, '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(amount) ? amount : 0;
}

function snapshotLineOccurrences(item, monthLength) {
  if (!item || !item.weekly) return 1;
  const day = snapshotDay(item);
  if (!day) return 1;
  return weeklyOccurrenceDays(day, monthLength).length;
}

function snapshotEndTotal(items, monthLength) {
  const total = (items || []).reduce((acc, item) => {
    return acc + snapshotAmount(item && item.value) * snapshotLineOccurrences(item, monthLength);
  }, 0);
  return roundCents(total);
}

function snapshotSlackTotal(items, monthLength) {
  const total = (items || []).reduce((acc, item) => {
    if (!item || !item.slack) return acc;
    return acc + Math.abs(snapshotAmount(item.value)) * snapshotLineOccurrences(item, monthLength);
  }, 0);
  return roundCents(total);
}

function projectionMonthLength(asOfDate) {
  const cycle = currentCycleRange(asOfDate);
  const [year, month] = cycle.end.split('-').map(Number);
  return daysInMonth(year, month);
}

function isProjectionFriday(asOfDate, day) {
  const dayNum = Number(day);
  if (!Number.isInteger(dayNum) || dayNum < 1 || dayNum > 31) return false;
  const cycle = currentCycleRange(asOfDate);
  const [year, month] = String(cycle.end || '').split('-').map(Number);
  if (!year || !month || dayNum > daysInMonth(year, month)) return false;
  return new Date(year, month - 1, dayNum).getDay() === 5;
}

function isFirstProjectionFriday(asOfDate, rows, index) {
  const list = rows || [];
  const row = list[index];
  if (!row || !isProjectionFriday(asOfDate, row.day)) return false;
  const day = Number(row.day);
  for (let earlier = 0; earlier < index; earlier += 1) {
    if (Number(list[earlier] && list[earlier].day) === day) return false;
  }
  return true;
}

function currentDayNumber(today) {
  if (today instanceof Date) return today.getDate();
  const day = Number(today);
  return Number.isInteger(day) ? day : new Date().getDate();
}

function isFirstCurrentDayRow(rows, index, today = new Date()) {
  const list = rows || [];
  const row = list[index];
  const todayDay = currentDayNumber(today);
  if (!row || Number(row.day) !== todayDay) return false;
  for (let earlier = 0; earlier < index; earlier += 1) {
    if (Number(list[earlier] && list[earlier].day) === todayDay) return false;
  }
  return true;
}

function weeklyOccurrenceDays(startDay, monthLength) {
  const limit = Math.min(monthLength || 31, 31);
  const days = [];
  for (let day = startDay; day <= limit; day += 7) days.push(day);
  return days;
}

function snapshotDay(item) {
  if (!item || item.date == null || item.date === '') return null;
  const day = parseInt(String(item.date).replace(/\D/g, ''), 10);
  if (!Number.isInteger(day) || day < 0 || day > 31) return null;
  return day;
}

function isBankBalanceLabel(label) {
  return String(label || '').trim().toLowerCase() === 'current bank balance';
}

function isOpeningBalance(item) {
  if (!isBankBalanceLabel(item && item.label)) return false;
  if (item.sequence == null || item.sequence === '') return false;
  const sequence = Number(item.sequence);
  return Number.isFinite(sequence) && sequence === 0;
}

function priorCycleDepositIncrement(previousByDay, day) {
  const dayNum = Number(day);
  if (!previousByDay || !Number.isInteger(dayNum) || dayNum < 1 || dayNum > 31) {
    return 0;
  }
  const at = (which) => {
    const value = Number(previousByDay[which]);
    return Number.isFinite(value) ? value : 0;
  };
  if (dayNum === 25) return roundCents(at(25));
  if (dayNum === 1) return roundCents(at(1) - at(31));
  return roundCents(at(dayNum) - at(dayNum - 1));
}

function knownThroughDay(options) {
  const today = options && options.today instanceof Date ? options.today : new Date();
  if (!options || !options.asOfDate) return today.getDate();
  const cycle = currentCycleRange(options.asOfDate);
  const [year, month] = String(cycle.end || '').split('-').map(Number);
  if (!year || !month) return today.getDate();
  const todayKey = isoDate(today.getFullYear(), today.getMonth() + 1, today.getDate());
  const monthStart = isoDate(year, month, 1);
  const monthEnd = isoDate(year, month, daysInMonth(year, month));
  if (todayKey < monthStart) return 0;
  if (todayKey > monthEnd) return 31;
  return today.getDate();
}

function isForecastExpense(amount) {
  if (amount == null || amount === '') return false;
  const value = Number(amount);
  return Number.isFinite(value) && value < 0;
}

function applyProjectedColumn(rows, options) {
  const todayDay = knownThroughDay(options || {});
  const previousByDay = options && options.previousByDay;
  let runningWhole = 0;
  let knownWhole = 0;
  rows.forEach((row) => {
    runningWhole += wholeAmount(row && row.amount);
    if (Number(row && row.day) <= todayDay) knownWhole = runningWhole;
  });
  let deposits = 0;
  let expenses = 0;
  let lastDay = null;
  rows.forEach((row) => {
    const day = Number(row.day);
    if (day <= todayDay) {
      row.projected = row.actual;
      row.negative = row.projected != null && row.projected < 0;
      return;
    }
    if (day !== lastDay) {
      deposits = roundCents(deposits + priorCycleDepositIncrement(previousByDay, day));
      lastDay = day;
    }
    if (isForecastExpense(row.amount)) {
      expenses = roundCents(expenses + wholeAmount(row.amount));
    }
    row.projected = roundCents(knownWhole + deposits + expenses);
    row.negative = row.projected < 0;
  });
  let lowest = null;
  rows.forEach((row) => {
    if (row.projected == null || row.projected === '') return;
    const value = roundCents(row.projected);
    if (lowest == null || value < lowest) lowest = value;
  });
  rows.forEach((row) => {
    row.lowest = row.projected != null
      && row.projected !== ''
      && lowest != null
      && roundCents(row.projected) === lowest;
  });
}

function buildCashFlowRows(depositByDay, items, monthLength = 31, options = null) {
  const detailsByDay = new Map();
  (items || []).forEach((item, itemIndex) => {
    const day = isOpeningBalance(item) ? 0 : snapshotDay(item);
    if (day == null) return;
    const days = item.weekly && day > 0
      ? weeklyOccurrenceDays(day, monthLength)
      : [day];
    days.forEach((occurrence) => {
      if (!detailsByDay.has(occurrence)) detailsByDay.set(occurrence, []);
      detailsByDay.get(occurrence).push({ item, itemIndex });
    });
  });
  const opening = detailsByDay.get(0) || [];
  opening.sort((left, right) => {
    const leftBank = isBankBalanceLabel(left.item && left.item.label);
    const rightBank = isBankBalanceLabel(right.item && right.item.label);
    if (leftBank === rightBank) return 0;
    return leftBank ? -1 : 1;
  });
  detailsByDay.set(0, opening);

  const rows = [];
  let runningAdjustment = 0;
  for (let day = 0; day <= 31; day += 1) {
    const deposit = Number(depositByDay && depositByDay[day]) || 0;
    const details = detailsByDay.get(day) || [];
    if (details.length === 0) {
      if (day === 0) continue;
      const projected = deposit + runningAdjustment;
      rows.push({
        day,
        rowId: `empty-${day}`,
        itemIndex: null,
        label: '',
        amount: null,
        slack: false,
        projected,
        negative: projected < 0,
      });
      continue;
    }
    details.forEach((detail) => {
      const amount = snapshotAmount(detail.item.value);
      runningAdjustment += amount;
      const projected = deposit + runningAdjustment;
      rows.push({
        day,
        rowId: `item-${detail.itemIndex}-${day}`,
        itemIndex: detail.itemIndex,
        label: detail.item.label || '',
        amount,
        slack: !!(detail.item && detail.item.slack),
        projected,
        negative: projected < 0,
      });
    });
  }
  let lowest = null;
  rows.forEach((row) => {
    const value = roundCents(row.projected);
    if (lowest == null || value < lowest) lowest = value;
  });
  rows.forEach((row) => {
    row.lowest = lowest != null && roundCents(row.projected) === lowest;
  });
  const actuals = cashFlowActuals(rows);
  const firstNegativeActual = actuals.findIndex(
    (value) => value != null && Number(value) < 0,
  );
  rows.forEach((row, index) => {
    row.actual = actuals[index];
    row.actualFirstNegative = index === firstNegativeActual;
  });
  if (options && options.today != null) applyProjectedColumn(rows, options);
  return rows;
}

function roundHalfAwayFromZero(amount) {
  const sign = amount < 0 ? -1 : 1;
  return sign * Math.round(Math.abs(amount));
}

function wholeAmount(amount) {
  if (amount == null || amount === '') return 0;
  const value = Number(amount);
  if (!Number.isFinite(value)) return 0;
  return roundHalfAwayFromZero(value);
}

function cashFlowActuals(rows) {
  let running = 0;
  return (rows || []).map((row, index) => {
    running += wholeAmount(row && row.amount);
    return index === 0 ? null : running;
  });
}

function priorCycleDayExists(asOfDate, day) {
  const dayNum = Number(day);
  if (!Number.isInteger(dayNum) || dayNum < 1 || dayNum > 31) return false;
  const range = previousCycleRange(asOfDate);
  const [year, month] = (dayNum >= 25 ? range.start : range.end)
    .split('-')
    .map(Number);
  return dayNum <= daysInMonth(year, month);
}

function currentCycleDepositTotal(asOfDate, transactions) {
  const cycle = currentCycleRange(asOfDate);
  const credits = {};
  (transactions || []).forEach((transaction) => {
    const date = transactionDate(transaction);
    if (!date || date < cycle.start || date > asOfDate) return;
    const credit = parseFloat(transaction.Credit ?? transaction.credit);
    credits[date] = roundCents(
      (credits[date] || 0) + (Number.isFinite(credit) ? credit : 0),
    );
  });
  let running = 0;
  let cursor = cycle.start.split('-').map(Number);
  const asOf = String(asOfDate).split('-').map(Number);
  while (compareYmd(cursor, asOf) <= 0) {
    const key = isoDate(cursor[0], cursor[1], cursor[2]);
    running = roundCents(running + (credits[key] || 0));
    cursor = nextYmd(cursor[0], cursor[1], cursor[2]);
  }
  return running;
}

function tooltipDollars(amount) {
  const dollars = Math.trunc(roundCents(Number(amount) || 0));
  const sign = dollars < 0 ? '-' : '';
  return `${sign}$${Math.abs(dollars).toLocaleString('en-US')}`;
}

function endingBalanceByProjectionDay(asOfDate, transactions) {
  const best = {};
  (transactions || []).forEach((transaction) => {
    const date = transactionDate(transaction);
    if (!date) return;
    const day = Number(date.slice(8, 10));
    if (projectionMonthDate(asOfDate, day) !== date) return;
    const id = Number(transaction.Id ?? transaction.id);
    if (!Number.isFinite(id)) return;
    if (!best[day] || id > best[day].id) {
      best[day] = {
        id,
        balance: transaction.Balance ?? transaction.balance,
      };
    }
  });
  const byDay = {};
  Object.keys(best).forEach((day) => {
    const balance = parseFloat(best[day].balance);
    if (Number.isFinite(balance)) byDay[day] = balance;
  });
  return byDay;
}

function projectedBalanceTooltip(asOfDate, day, previousByDay, currentTotal, actualByDay) {
  const hasPrev = previousByDay && priorCycleDayExists(asOfDate, day);
  const hasCurr = currentTotal != null && currentTotal !== '';
  const actual = actualByDay && (actualByDay[day] ?? actualByDay[Number(day)]);
  const hasActu = actual != null && actual !== '' && Number.isFinite(Number(actual));
  if (!hasPrev && !hasCurr && !hasActu) return '';
  if (!hasPrev && !hasCurr) return `actu: ${tooltipDollars(actual)}`;
  const prev = hasPrev ? tooltipDollars(previousByDay[day]) : '';
  const curr = hasCurr ? tooltipDollars(currentTotal) : '';
  const lines = [`prev: ${prev}`, `curr: ${curr}`];
  if (hasActu) lines.push(`actu: ${tooltipDollars(actual)}`);
  return lines.join('\n');
}

function formatProjectionMoney(amount) {
  if (amount == null || amount === '') return '';
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(roundHalfAwayFromZero(value));
}

function formatProjectionAmount(amount) {
  return formatProjectionMoney(amount);
}

function isPositiveCashFlowAmount(amount) {
  if (amount == null || amount === '') return false;
  const value = Number(amount);
  return Number.isFinite(value) && value > 0;
}

function isLargeCashFlowExpense(amount) {
  if (amount == null || amount === '') return false;
  const value = Number(amount);
  return Number.isFinite(value) && value <= -10000;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function canEditCashFlowRow(row, role) {
  return Number(role) === 1 && row != null && row.itemIndex != null;
}

function commitCashAmount(raw) {
  let value = String(raw ?? '').toUpperCase().replace(/,/g, '').replace(/=/g, '');
  let numericValue = 0;
  if (/[+\-*]/.test(value)) {
    value = value.replace(/(\d+(\.\d+)?)[K]/gi, (match, number) => {
      return parseFloat(number) * 1000;
    });
    try {
      const result = eval(value);
      numericValue = isNaN(result) ? 0 : result;
    } catch {
      numericValue = 0;
    }
  } else {
    numericValue = value.endsWith('K')
      ? parseFloat(value.slice(0, -1)) * 1000
      : parseFloat(value);
    if (isNaN(numericValue)) numericValue = 0;
  }
  return new Intl.NumberFormat('en-US', {
    signDisplay: 'auto',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);
}

function commitCashFlowRowEdit(item, draft) {
  const next = { ...(item && typeof item === 'object' ? item : {}) };
  const fields = draft || {};
  next.date = commitCashItemDay(fields.day);
  const label = String(fields.label ?? '').trim();
  next.label = label || 'Label';
  next.value = commitCashAmount(fields.amount);
  return next;
}

function filterCashFlowRows(rows, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return rows || [];
  return (rows || []).filter((row) => {
    const haystack = [
      row && row.day,
      row && row.label,
      formatProjectionAmount(row && row.amount),
      formatProjectionAmount(row && row.actual),
      formatProjectionMoney(row && row.projected),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
}

function cashFlowDescriptionWord(label) {
  const word = String(label ?? '')
    .trim()
    .split(/\s+/)[0];
  return word || '';
}

function descriptionStartsWithWord(label, word) {
  const text = String(label ?? '')
    .trim()
    .toLowerCase();
  const needle = String(word ?? '')
    .trim()
    .toLowerCase();
  if (!needle) return false;
  return text.startsWith(needle);
}

/** Rows whose description starts with this description's first word. */
function cashFlowDescriptionPreview(rows, label) {
  const word = cashFlowDescriptionWord(label);
  if (!word) return [];
  return (rows || []).filter((row) =>
    descriptionStartsWithWord(row && row.label, word),
  );
}

/** Sum of the amounts shown for those preview rows, as $1,234 or -$1,234. */
function cashFlowDescriptionPreviewTotal(rows) {
  const total = (rows || []).reduce(
    (sum, row) => sum + wholeAmount(row && row.amount),
    0,
  );
  return tooltipDollars(total);
}

function cashFlowPrintDocument(title, rows, asOfDate) {
  const list = rows || [];
  let lowest = null;
  list.forEach((row) => {
    const value = roundCents(Number(row.projected));
    if (!Number.isFinite(value)) return;
    if (lowest == null || value < lowest) lowest = value;
  });
  const seenDays = new Set();
  const actuals = cashFlowActuals(list);
  const firstNegativeActual = actuals.findIndex(
    (value) => value != null && Number(value) < 0,
  );
  const body = list
    .map((row, index) => {
      const value = roundCents(Number(row.projected));
      const projectedClass = lowest != null && value === lowest
        ? 'num lowest'
        : row.negative
          ? 'num negative'
          : 'num';
      const actualClass = index === firstNegativeActual
        ? 'num actual-first-negative'
        : 'num';
      const dayNum = Number(row.day);
      const firstOfDay = !seenDays.has(dayNum);
      seenDays.add(dayNum);
      const dayClass = firstOfDay && isProjectionFriday(asOfDate, row.day)
        ? 'day friday'
        : 'day';
      const amountClass = row.slack ? 'num slack' : 'num';
      return `<tr><td class="${dayClass}">${escapeHtml(row.day)}</td><td>${escapeHtml(row.label)}</td><td class="${amountClass}">${escapeHtml(formatProjectionAmount(row.amount))}</td><td class="${actualClass}">${escapeHtml(formatProjectionAmount(actuals[index]))}</td><td class="${projectedClass}">${escapeHtml(formatProjectionMoney(row.projected))}</td></tr>`;
    })
    .join('');
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
@page { size: portrait; margin: 0.5in; }
body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #222; }
h1 { font-size: 16pt; font-weight: 600; margin: 0 0 12px; }
table { width: 100%; border-collapse: collapse; font-size: 10pt; }
th, td { border-bottom: 1px solid #ccc; padding: 4px 6px; text-align: left; vertical-align: top; }
th { background: #eee; }
td.num { text-align: right; white-space: nowrap; }
td.num.slack { background: #ececec; }
td.num.negative { background: #fde8e8; color: #c0392b; }
td.num.lowest { background: #fff0e0; color: #e67e22; }
td.num.actual-first-negative { background: #fff0e0; color: #e67e22; }
th.day, td.day { text-align: center; padding-left: 1px; padding-right: 1px; }
th.money { text-align: right; }
td.friday { background: #d4d4d4; }
</style>
</head>
<body>
<h1>${escapeHtml(title)}</h1>
<table>
<thead><tr><th class="day">Day</th><th>Description</th><th class="money">Amount</th><th class="act money">Act</th><th class="money">Projected</th></tr></thead>
<tbody>${body}</tbody>
</table>
</body>
</html>`;
}

module.exports = {
  previousCycleRange,
  currentCycleRange,
  nextIsoDate,
  cumulativeDepositByDay,
  forecastDepositByDay,
  projectionMonthLength,
  buildCashFlowRows,
  priorCycleDepositIncrement,
  formatProjectionMoney,
  formatProjectionAmount,
  cashFlowActuals,
  cashFlowPrintDocument,
  filterCashFlowRows,
  cashFlowDescriptionWord,
  cashFlowDescriptionPreview,
  cashFlowDescriptionPreviewTotal,
  isProjectionFriday,
  isFirstProjectionFriday,
  isFirstCurrentDayRow,
  isPositiveCashFlowAmount,
  isLargeCashFlowExpense,
  currentCycleDepositTotal,
  endingBalanceByProjectionDay,
  projectionFetchEnd,
  projectedBalanceTooltip,
  snapshotEndTotal,
  snapshotSlackTotal,
  canEditCashFlowRow,
  commitCashFlowRowEdit,
  cashFlowAsOfLabel,
};
